/**
 * Shared "which field participant is this command for" resolution —
 * backlog §9.4 (2026-09-08).
 *
 * Every operator-facing camera/capture command surface (SnapshotButton,
 * OperatorToolbar, CameraControl) and the embed-host command bridge
 * (EmbedRoomCommands) used to each independently pick "the field
 * participant" via `participants.find((p) => !p.isLocal && ...)` — the
 * FIRST remote non-bot participant in whatever order LiveKit's
 * `useParticipants()` currently returns. That order is not guaranteed
 * stable: once a 3rd field participant joins, whichever one `.find()`
 * used to land on isn't guaranteed to still be first, so a command
 * addressed to "the field participant" could silently retarget
 * mid-session. This is the confirmed root cause behind the reported
 * "adding a 3rd participant breaks the 2nd's camera/photo access."
 *
 * Fix, in two parts:
 *  1. `useFieldParticipants` sorts field-role remote participants by
 *     `joinedAt` (ties broken on identity) so the DEFAULT choice is
 *     deterministic — "whoever joined first" — and stays stable as
 *     long as that participant remains in the room, regardless of
 *     LiveKit's internal array ordering.
 *  2. `useFieldParticipantSelection` layers an EXPLICIT operator
 *     override on top of that default. `showPicker` is only true once
 *     there are 2+ field participants — the common single-field-
 *     participant case renders and behaves exactly as it did before
 *     this hook existed (no picker, `selected` is that one
 *     participant).
 */
import type { Participant } from "livekit-client";
import { ParticipantKind } from "livekit-client";
import { useMemo, useState } from "react";

/** Non-local, non-bot (Egress/Ingress/SIP/Agent) remote participant —
 *  i.e. a human on the field side who could plausibly run a
 *  FieldCaptureHandler / FieldCommandsHandler and ack a command. */
export function isFieldParticipant(p: Participant): boolean {
  return (
    !p.isLocal &&
    (p.kind === undefined || p.kind === ParticipantKind.STANDARD)
  );
}

function byJoinOrder(a: Participant, b: Participant): number {
  const at = a.joinedAt?.getTime() ?? 0;
  const bt = b.joinedAt?.getTime() ?? 0;
  if (at !== bt) return at - bt;
  return a.identity.localeCompare(b.identity);
}

/** `participant:<uuid>` is the identity scheme for every non-operator
 *  room member (see RoomLayout.tsx) — strip the prefix so callers can
 *  key into `useParticipantRoleLookup`'s `byParticipantIdName` map for
 *  a human-readable label. Returns null for anything else (e.g. an
 *  operator's own `user:<id>` identity, never a field participant). */
export function participantUuidFromIdentity(identity: string): string | null {
  return identity.startsWith("participant:")
    ? identity.slice("participant:".length)
    : null;
}

export function useFieldParticipants(
  participants: Participant[],
): Participant[] {
  return useMemo(
    () => participants.filter(isFieldParticipant).sort(byJoinOrder),
    [participants],
  );
}

export type FieldParticipantSelection = {
  fieldParticipants: Participant[];
  /** The participant a command should target right now — the explicit
   *  operator pick if one is live, else the join-order default, else
   *  null when the room has no field participant at all. */
  selected: Participant | null;
  selectedIdentity: string | null;
  setSelectedIdentity: (identity: string) => void;
  /** True once there's more than one candidate — callers render
   *  `FieldParticipantPicker` only when this is true, so a single-
   *  field-participant session shows no picker at all. */
  showPicker: boolean;
};

export function useFieldParticipantSelection(
  participants: Participant[],
): FieldParticipantSelection {
  const fieldParticipants = useFieldParticipants(participants);
  const [selectedIdentity, setSelectedIdentity] = useState<string | null>(
    null,
  );

  // If the operator's previous pick left the room (or none was made
  // yet), fall back to the deterministic join-order default rather
  // than leaving the command with no addressee.
  const selected =
    fieldParticipants.find((p) => p.identity === selectedIdentity) ??
    fieldParticipants[0] ??
    null;

  return {
    fieldParticipants,
    selected,
    selectedIdentity: selected?.identity ?? null,
    setSelectedIdentity,
    showPicker: fieldParticipants.length > 1,
  };
}
